/*
 *  ___                  __                __  _ _
 *   | |_  _     _ |_ |_|_    _  _ _ _ _  /  \(_(_|. _  _
 *   | | )(_)|_|(_)| )|_|__)(|_)| (-_)_)  \__/| | ||| )(-
 *              _/           |
 */

import { updateItemIn } from '../../../../src/core/dataitems'
import {
  hasValue,
  validEmail,
  verify,
  validAmountRange,
  validMinAmount,
} from '../../../../src/core/validations'

import {
  validVotersID,
  validSSNIT,
  validPassport,
  validDrivers,
  validOldDrivers,
  validNHIS,
  validID,
  validName,
  validCellnumber,
} from '../../validations'
import dayjs from 'dayjs'

const now = dayjs()

const benefitAgeLimits = {
  death: { minEntry: 18, maxEntry: 70, maxCease: 80 },
  tpd: { minEntry: 18, maxEntry: 54, maxCease: 60 },
  ci: { minEntry: 18, maxEntry: 70, maxCease: 80 },
}

const benefitLabel = {
  death: 'Death Benefit',
  tpd: 'Disability Related Benefit',
  ci: 'Critical Illness',
}

const parseIntOrNaN = (v) => {
  const n = parseInt(String(v), 10)
  return Number.isFinite(n) ? n : NaN
}

const selectedBenefitsOnQuoteScreen = (state) => {
  const b = new Set()
  const opt = state.form['benefit-option']
  if (opt === 'Accelerated') {
    b.add('death')
    const accel = state.form['accelerated-options'] || {}
    const hasTpd =
      accel['death-and-total-personal-disability'] === 'Y' ||
      accel['death-and-total-disability-and-critical-illness'] === 'Y' ||
      accel['total-personal-disability-and-critical-illness'] === 'Y'
    const hasCi =
      accel['death-and-critical-illness'] === 'Y' ||
      accel['death-and-total-disability-and-critical-illness'] === 'Y' ||
      accel['total-personal-disability-and-critical-illness'] === 'Y'
    if (hasTpd) b.add('tpd')
    if (hasCi) b.add('ci')
    return b
  }

  if (opt === 'Standalone') {
    const standalone = state.form['standalone-options'] || {}
    if (hasValue(standalone['death-sum-assured'])) b.add('death')
    if (hasValue(standalone['total-personal-disability-sum-assured']))
      b.add('tpd')
    if (hasValue(standalone['illness-sum-assured'])) b.add('ci')
    return b
  }

  return b
}

const quoteBasicTermYears = (state) => {
  const opt = state.form['benefit-option']
  if (opt === 'Standalone') {
    return parseIntOrNaN(
      state.form['standalone-options'] &&
        state.form['standalone-options']['standalone-term']
    )
  }
  if (opt === 'Accelerated') {
    return parseIntOrNaN(
      state.form['accelerated-options'] &&
        state.form['accelerated-options']['accelerated-term']
    )
  }
  return NaN
}

const ageAtEntryYears = (dob) =>
  hasValue(dob) ? dayjs().diff(dob, 'year') : NaN

const ageLimitsForSelectedBenefits = (benefits) => {
  const effective =
    benefits && benefits.size > 0 ? benefits : new Set(['death'])
  let minEntry = 0
  let maxEntry = Infinity
  let maxCease = Infinity
  let maxEntryBy = []
  let maxCeaseBy = []
  effective.forEach((k) => {
    const lim = benefitAgeLimits[k]
    if (!lim) return
    minEntry = Math.max(minEntry, lim.minEntry)
    if (lim.maxEntry < maxEntry) {
      maxEntry = lim.maxEntry
      maxEntryBy = [k]
    } else if (lim.maxEntry === maxEntry) {
      maxEntryBy.push(k)
    }
    if (lim.maxCease < maxCease) {
      maxCease = lim.maxCease
      maxCeaseBy = [k]
    } else if (lim.maxCease === maxCease) {
      maxCeaseBy.push(k)
    }
  })

  const labelList = (keys) =>
    (keys || [])
      .map((k) => benefitLabel[k] || String(k))
      .filter(Boolean)
      .join(', ')

  return {
    minEntry,
    maxEntry,
    maxCease,
    maxEntryByLabel: labelList(maxEntryBy),
    maxCeaseByLabel: labelList(maxCeaseBy),
  }
}

const maxBasicTermByAge = (state, limits) => {
  const mainDob = state.form['life-assured-identification']['date-of-birth']
  const mainAge = ageAtEntryYears(mainDob)
  if (!Number.isFinite(mainAge)) return NaN
  let maxTerm = Math.floor(limits.maxCease - mainAge)

  const spouseDob =
    state.form['spouse-identification'] &&
    state.form['spouse-identification']['date-of-birth']
  const includeSpouse =
    state.form['life-assured-identification'] &&
    state.form['life-assured-identification']['marital-status'] === 'Married' &&
    state.form['joint-life'] === 'Y' &&
    hasValue(spouseDob)
  if (includeSpouse) {
    const spouseAge = ageAtEntryYears(spouseDob)
    if (Number.isFinite(spouseAge)) {
      maxTerm = Math.min(maxTerm, Math.floor(limits.maxCease - spouseAge))
    }
  }
  return Number.isFinite(maxTerm) ? maxTerm : NaN
}

const parentOrInLawRelationships = new Set([
  'Mother',
  'Mother in law',
  'Father',
  'Father in law',
])

const isForcesPayOfficeForm = (form) =>
  form['payment-method-selection'] &&
  form['payment-method-selection']['stop-order'] === 'Y' &&
  form['payment-employment-details'] &&
  form['payment-employment-details']['employer-organisation'] ===
    'Forces Pay Office'

export const validate = (state, requiredOnly, validateOnly) => {
  if (process.env.DISABLE_VALIDATIONS === 'true') {
    return true
  }

  let validates = true
  switch (state.step) {
    case 'quotation-details':
      const quoteBenefits = selectedBenefitsOnQuoteScreen(state)
      const quoteLimits = ageLimitsForSelectedBenefits(quoteBenefits)
      const quoteTermYears = quoteBasicTermYears(state)

      const mainDob = state.form['life-assured-identification']['date-of-birth']
      const mainAge = ageAtEntryYears(mainDob)
      const mainEntryOk =
        hasValue(mainDob) &&
        (requiredOnly ||
          (Number.isFinite(mainAge) &&
            mainAge >= quoteLimits.minEntry &&
            mainAge <= quoteLimits.maxEntry))

      validates =
        verify(
          state,
          mainEntryOk ? 'Y' : null,
          hasValue,
          !validateOnly && { id: 'date-of-birth' },
          requiredOnly
            ? ['*']
            : [
                `Age at entry must be between ${quoteLimits.minEntry} and ${quoteLimits.maxEntry} (limited by ${quoteLimits.maxEntryByLabel}).`,
              ]
        ) && validates

      if (!requiredOnly && mainEntryOk && Number.isFinite(quoteTermYears)) {
        const ceaseAge = mainAge + quoteTermYears
        if (Number.isFinite(ceaseAge) && ceaseAge > quoteLimits.maxCease) {
          validates =
            verify(
              state,
              ceaseAge,
              () => false,
              !validateOnly && { id: 'date-of-birth' },
              [
                `Age at cover cease exceeded: entry age + term must be ≤ ${quoteLimits.maxCease} (limited by ${quoteLimits.maxCeaseByLabel}).`,
              ]
            ) && validates
        }
      }

      //gender
      validates =
        verify(
          state,
          state.form['life-assured-identification']['gender'],
          hasValue,
          !validateOnly && { id: 'gender' },
          ['*']
        ) && validates

      //marital-status
      validates =
        verify(
          state,
          state.form['life-assured-identification']['marital-status'],
          hasValue,
          !validateOnly && { id: 'marital-status' },
          ['*']
        ) && validates

      // joint-life (required when married / visible)
      if (
        state.form['life-assured-identification']['marital-status'] ===
        'Married'
      ) {
        validates =
          verify(
            state,
            state.form['joint-life'],
            hasValue,
            !validateOnly && { id: 'joint-life-radio-group' },
            ['*']
          ) && validates
      }

      // spouse date-of-birth (required when married AND joint-life selected)
      if (
        state.form['life-assured-identification']['marital-status'] ===
          'Married' &&
        state.form['joint-life'] === 'Y'
      ) {
        const spouseDob = state.form['spouse-identification']['date-of-birth']
        const spouseAge = ageAtEntryYears(spouseDob)
        const spouseEntryOk =
          hasValue(spouseDob) &&
          (requiredOnly ||
            (Number.isFinite(spouseAge) &&
              spouseAge >= quoteLimits.minEntry &&
              spouseAge <= quoteLimits.maxEntry))

        validates =
          verify(
            state,
            spouseEntryOk ? 'Y' : null,
            hasValue,
            !validateOnly && { id: 'date-of-birth', uid: '0d8e0852303cf721' },
            requiredOnly
              ? ['*']
              : [
                  `Spouse age at entry must be between ${quoteLimits.minEntry} and ${quoteLimits.maxEntry} (limited by ${quoteLimits.maxEntryByLabel}).`,
                ]
          ) && validates

        if (!requiredOnly && spouseEntryOk && Number.isFinite(quoteTermYears)) {
          const spouseCeaseAge = spouseAge + quoteTermYears
          if (
            Number.isFinite(spouseCeaseAge) &&
            spouseCeaseAge > quoteLimits.maxCease
          ) {
            validates =
              verify(
                state,
                spouseCeaseAge,
                () => false,
                !validateOnly && {
                  id: 'date-of-birth',
                  uid: '0d8e0852303cf721',
                },
                [
                  `Spouse age at cover cease exceeded: entry age + term must be ≤ ${quoteLimits.maxCease} (limited by ${quoteLimits.maxCeaseByLabel}).`,
                ]
              ) && validates
          }
        }
      }

      //benefit-option
      validates =
        verify(
          state,
          state.form['benefit-option'],
          hasValue,
          !validateOnly && { id: 'benefit-option' },
          ['*']
        ) && validates

      // premium updates (required on quote screen)
      validates =
        verify(
          state,
          state.form['premium-updates'],
          hasValue,
          !validateOnly && { id: 'update-option', type: 'field' },
          ['*']
        ) && validates

      //TO DO: ADD VALIDATION FOR TOTAL SUM ASSURED
      //Validate values of Benefit Option Fields
      if (state.form['benefit-option'] === 'Standalone') {
        const maxTerm = maxBasicTermByAge(state, quoteLimits)
        validates =
          verify(
            state,
            state.form['standalone-options']['standalone-term'],
            (v) => {
              if (!hasValue(v)) return false
              const t = parseInt(String(v), 10)
              if (!Number.isFinite(t)) return false
              if (requiredOnly) return true
              if (t < 5) return false
              return Number.isFinite(maxTerm) ? t <= maxTerm : true
            },
            !validateOnly && { id: 'standalone-term' },
            requiredOnly
              ? ['*']
              : [
                  Number.isFinite(maxTerm)
                    ? `Term must be between 5 and ${maxTerm} years (limited by ${quoteLimits.maxCeaseByLabel}).`
                    : 'Term must be at least 5 years.',
                ]
          ) && validates

        // If main-life funeral cover selected, basic benefit term must be at least 10 years
        if (state.form['funeral-cover-for-main-life'] === 'Y') {
          validates =
            verify(
              state,
              state.form['standalone-options']['standalone-term'],
              (v) => {
                if (!hasValue(v)) return false
                if (requiredOnly) return true
                const t = parseInt(String(v), 10)
                return Number.isFinite(t) && t >= 10
              },
              !validateOnly && { id: 'standalone-term' },
              requiredOnly
                ? ['*']
                : [
                    'Basic benefit term must be at least 10 years when Funeral Cover is selected.',
                  ]
            ) && validates
        }

        const standalone = state.form['standalone-options'] || {}
        const toAmount = (v) => {
          if (!hasValue(v)) return 0
          const n = parseFloat(String(v).replace(/,/g, ''))
          return Number.isFinite(n) ? n : 0
        }

        const death = toAmount(standalone['death-sum-assured'])
        const tpd = toAmount(
          standalone['total-personal-disability-sum-assured']
        )
        const illness = toAmount(standalone['illness-sum-assured'])

        const selected = death > 0 || tpd > 0 || illness > 0
        if (!selected) {
          validates =
            verify(
              state,
              null,
              hasValue,
              !validateOnly && { id: 'death-sum-assured' },
              ['Please enter at least one Standalone benefit amount.']
            ) && validates
        }

        ;[
          ['death-sum-assured', standalone['death-sum-assured']],
          [
            'total-personal-disability-sum-assured',
            standalone['total-personal-disability-sum-assured'],
          ],
          ['illness-sum-assured', standalone['illness-sum-assured']],
        ].forEach(([field, value]) => {
          if (hasValue(value)) {
            validates =
              verify(
                state,
                value,
                (v) => (requiredOnly ? hasValue(v) : validMinAmount(v, 100000)),
                !validateOnly && { id: field },
                requiredOnly
                  ? ['*']
                  : [
                      'Sum Assured for each option must be at least GHc 100,000.00',
                    ]
              ) && validates
          }
        })

        const totalStandalone = death + tpd + illness
        if (!requiredOnly && totalStandalone > 5000000) {
          validates =
            verify(
              state,
              totalStandalone,
              () => false,
              !validateOnly && { id: 'death-sum-assured' },
              ['Total Standalone benefits must not exceed GHc 5,000,000.00']
            ) && validates
        }
      }

      if (state.form['benefit-option'] === 'Accelerated') {
        const selected =
          state.form['accelerated-options'][
            'death-and-total-personal-disability'
          ] === 'Y' ||
          state.form['accelerated-options']['death-and-critical-illness'] ===
            'Y' ||
          state.form['accelerated-options'][
            'death-and-total-disability-and-critical-illness'
          ] === 'Y' ||
          state.form['accelerated-options'][
            'total-personal-disability-and-critical-illness'
          ] === 'Y'

        validates =
          verify(
            state,
            selected ? 'Y' : null,
            hasValue,
            !validateOnly && { id: 'death-and-total-personal-disability' },
            ['Please choose an accelerated benefit option.']
          ) && validates

        validates =
          verify(
            state,
            state.form['accelerated-options']['accelerated-sum-assured'],
            (v) => (requiredOnly ? hasValue(v) : validMinAmount(v, 100000)),
            !validateOnly && { id: 'accelerated-sum-assured' },
            requiredOnly
              ? ['*']
              : ['Total Sum Assured must be at least GHc 100,000.00']
          ) && validates

        // Max accelerated sum assured
        const acceleratedValue =
          state.form['accelerated-options']['accelerated-sum-assured']
        const acceleratedParsed = hasValue(acceleratedValue)
          ? parseFloat(String(acceleratedValue).replace(/,/g, ''))
          : NaN
        const shouldApplyAcceleratedMax =
          !requiredOnly &&
          Number.isFinite(acceleratedParsed) &&
          acceleratedParsed >= 100000
        validates =
          verify(
            state,
            acceleratedValue,
            () =>
              shouldApplyAcceleratedMax ? acceleratedParsed <= 5000000 : true,
            // Only write max-cap errors when the min check already passed,
            // otherwise we'd clear the min error by writing `errors: []`.
            !validateOnly &&
              shouldApplyAcceleratedMax && { id: 'accelerated-sum-assured' },
            requiredOnly
              ? ['*']
              : ['Total Sum Assured must not exceed GHc 5,000,000.00']
          ) && validates

        validates =
          verify(
            state,
            state.form['accelerated-options']['accelerated-term'],
            (v) => {
              if (!hasValue(v)) return false
              const t = parseInt(String(v), 10)
              if (!Number.isFinite(t)) return false
              if (requiredOnly) return true
              if (t < 5) return false
              const maxTerm = maxBasicTermByAge(state, quoteLimits)
              return Number.isFinite(maxTerm) ? t <= maxTerm : true
            },
            !validateOnly && { id: 'accelerated-term' },
            requiredOnly
              ? ['*']
              : [
                  (() => {
                    const maxTerm = maxBasicTermByAge(state, quoteLimits)
                    return Number.isFinite(maxTerm)
                      ? `Term must be between 5 and ${maxTerm} years (limited by ${quoteLimits.maxCeaseByLabel}).`
                      : 'Term must be at least 5 years.'
                  })(),
                ]
          ) && validates

        // If main-life funeral cover selected, basic benefit term must be at least 10 years
        if (state.form['funeral-cover-for-main-life'] === 'Y') {
          validates =
            verify(
              state,
              state.form['accelerated-options']['accelerated-term'],
              (v) => {
                if (!hasValue(v)) return false
                if (requiredOnly) return true
                const t = parseInt(String(v), 10)
                return Number.isFinite(t) && t >= 10
              },
              !validateOnly && { id: 'accelerated-term' },
              requiredOnly
                ? ['*']
                : [
                    'Basic benefit term must be at least 10 years when Funeral Cover is selected.',
                  ]
            ) && validates
        }
      }
      break

    case 'additional-benefits':
      const quoteTermRaw =
        state.form['benefit-option'] === 'Standalone'
          ? state.form['standalone-options']['standalone-term']
          : state.form['benefit-option'] === 'Accelerated'
          ? state.form['accelerated-options']['accelerated-term']
          : null
      const quoteTerm = quoteTermRaw ? parseInt(String(quoteTermRaw), 10) : NaN

      //Educational Support Benefit validation (only if values are entered)
      if (
        state.form['educational-support-benefit']['educational-support-benefit']
      ) {
        validates =
          verify(
            state,
            state.form['educational-support-benefit'][
              'educational-support-benefit'
            ],
            (v) => {
              if (!hasValue(v)) return false
              if (requiredOnly) return true
              const n = parseFloat(String(v).replace(/,/g, ''))
              return Number.isFinite(n) && n <= 100000
            },
            !validateOnly && { id: 'educational-support-benefit' },
            requiredOnly
              ? ['*']
              : ['The maximum Educational Support Benefit is GHc 100,000.00']
          ) && validates

        validates =
          verify(
            state,
            state.form['educational-support-benefit'][
              'educational-support-term'
            ],
            (v) => {
              if (!hasValue(v)) return false
              const t = parseInt(String(v), 10)
              if (!Number.isFinite(t)) return false
              if (requiredOnly) return true
              if (t < 5) return false
              if (Number.isFinite(quoteTerm) && t > quoteTerm) return false
              return true
            },
            !validateOnly && { id: 'educational-support-term' },
            requiredOnly
              ? ['*']
              : [
                  Number.isFinite(quoteTerm)
                    ? `Educational support term must be between 5 and ${quoteTerm} years.`
                    : 'Educational support term must be at least 5 years.',
                ]
          ) && validates
      }

      //Long term savings validation (only if value is entered)
      if (state.form['long-term-savings']['long-term-savings-premium']) {
        validates =
          verify(
            state,
            state.form['long-term-savings']['long-term-savings-premium'],
            (v) => {
              if (!hasValue(v)) return false
              if (requiredOnly) return true
              const n = parseFloat(String(v).replace(/,/g, ''))
              return Number.isFinite(n) && n >= 200 && n <= 5000
            },
            !validateOnly && { id: 'long-term-savings-premium' },
            requiredOnly
              ? ['*']
              : [
                  'The long term savings premium must be between GHc 200 and 5,000.00',
                ]
          ) && validates

        validates =
          verify(
            state,
            state.form['long-term-savings']['savings-term'],
            (v) => {
              if (!hasValue(v)) return false
              const t = parseInt(String(v), 10)
              if (!Number.isFinite(t)) return false
              if (requiredOnly) return true
              if (t < 5) return false
              if (Number.isFinite(quoteTerm) && t > quoteTerm) return false
              return true
            },
            !validateOnly && { id: 'savings-term' },
            requiredOnly
              ? ['*']
              : [
                  Number.isFinite(quoteTerm)
                    ? `Savings term must be between 5 and ${quoteTerm} years.`
                    : 'Savings term must be at least 5 years.',
                ]
          ) && validates
      }

      // Funeral cover required selections (only if available from Quote screen)
      if (state.form['funeral-cover-for-main-life'] === 'Y') {
        const coverSelected = Object.keys(state.form['funeral-cover-options'])
          .filter((k) => k.startsWith('ghs-'))
          .some((k) => state.form['funeral-cover-options'][k] === 'Y')

        validates =
          verify(
            state,
            coverSelected ? 'Y' : null,
            hasValue,
            !validateOnly && { id: 'ghs-5000' },
            ['Please select a cover in order to continue.']
          ) && validates

        const termSelected =
          state.form['funeral-cover-options']['term-ten'] === 'Y' ||
          state.form['funeral-cover-options']['term-fifteen'] === 'Y'

        validates =
          verify(
            state,
            termSelected ? 'Y' : null,
            hasValue,
            !validateOnly && { id: 'term-ten' },
            ['Please select the term for your funeral policy.']
          ) && validates

        // The basic benefit term cannot be shorter than the funeral policy term
        const funeralTerm =
          state.form['funeral-cover-options']['term-fifteen'] === 'Y'
            ? 15
            : state.form['funeral-cover-options']['term-ten'] === 'Y'
            ? 10
            : NaN

        const funeralTermFieldId =
          state.form['funeral-cover-options']['term-fifteen'] === 'Y'
            ? 'term-fifteen'
            : 'term-ten'

        if (
          !requiredOnly &&
          Number.isFinite(funeralTerm) &&
          Number.isFinite(quoteTerm)
        ) {
          validates =
            verify(
              state,
              quoteTerm,
              (t) => t >= funeralTerm,
              !validateOnly && { id: 'policy-term-radio-group' },
              [
                'The basic benefit term cannot be shorter than the funeral policy term.',
              ]
            ) && validates
        }
      }
      break

    case 'family-members-age': {
      validates =
        verify(
          state,
          state.local && state.local['age-of-member'],
          hasValue,
          !validateOnly && { id: 'age-of-member' },
          ['*']
        ) && validates

      validates =
        verify(
          state,
          state.local && state.local.gender,
          hasValue,
          !validateOnly && { id: 'gender' },
          ['*']
        ) && validates

      // Require at least one benefit (funeral, transition, or old-age for parents/in-laws)
      const loc = state.local || {}
      const hasBenefit =
        loc['funeral-cover'] === 'Y' ||
        (state.form['transition-plan'] && loc['transition-cover'] === 'Y') ||
        (parentOrInLawRelationships.has(loc.relationship) &&
          loc['old-age-cover'] === 'Y')

      validates =
        verify(
          state,
          hasBenefit ? 'Y' : null,
          hasValue,
          !validateOnly && { id: 'funeral-cover' },
          ['Please select at least one benefit for this life.']
        ) && validates

      // If old age cover is selected, ensure relationship is eligible (parents/in-laws)
      if (loc['old-age-cover'] === 'Y') {
        validates =
          verify(
            state,
            loc.relationship,
            (r) => {
              if (!hasValue(r)) return false
              if (r === 'Mother' || r === 'Father') return true
              if (
                (r === 'Mother in law' || r === 'Father in law') &&
                state.form &&
                state.form['joint-life'] === 'Y'
              )
                return true
              return false
            },
            !validateOnly && { id: 'old-age-cover' },
            [
              state.form && state.form['joint-life'] === 'Y'
                ? 'Old Age cover is only available for parents and in-laws.'
                : 'Old Age cover is only available for parents (and in-laws only when Joint Life is selected).',
            ]
          ) && validates
      }
      break
    }

    case 'family-members-quotation-details': {
      const loc = state.local || {}
      if (loc['funeral-cover'] === 'Y') {
        validates =
          verify(
            state,
            loc['funeral-cover-benefit'],
            hasValue,
            !validateOnly && { id: 'funeral-cover-benefit' },
            ['*']
          ) && validates

        // If main life did NOT select funeral cover, additional life must choose a term.
        if (
          !(state.form && state.form['funeral-cover-for-main-life'] === 'Y')
        ) {
          validates =
            verify(
              state,
              loc['funeral-cover-term'],
              (v) => v === '10' || v === '15',
              !validateOnly && { id: 'additional-life-term-ten' },
              ['*']
            ) && validates
        }
      }

      if (loc['old-age-cover'] === 'Y') {
        const min = (state) => {
          const form = state.form || {}
          const benefitOption = form['benefit-option']
          const num = (v) => {
            const n =
              typeof v === 'number' ? v : parseFloat(`${v}`.replace(/,/g, ''))
            return Number.isFinite(n) ? n : 0
          }
          let base = 0
          if (benefitOption === 'Accelerated') {
            base = num(
              (form['accelerated-options'] || {})['accelerated-sum-assured']
            )
          } else {
            const s = form['standalone-options'] || {}
            base =
              num(s['death-sum-assured']) +
              num(s['total-personal-disability-sum-assured']) +
              num(s['illness-sum-assured'])
          }
          return base > 0 ? base * 0.1 : 0
        }

        const minValue = min(state)
        validates =
          verify(
            state,
            loc['old-age-cover-benefit'],
            (v) => {
              if (!hasValue(v)) return false
              const n =
                typeof v === 'number' ? v : parseFloat(`${v}`.replace(/,/g, ''))
              return Number.isFinite(n) && (minValue === 0 || n >= minValue)
            },
            !validateOnly && { id: 'old-age-cover-benefit' },
            minValue === 0
              ? ['*']
              : [
                  `Old Age cover benefit must be at least 10% of the main life sum assured (${minValue.toFixed(
                    2
                  )}).`,
                ]
          ) && validates
      }
      break
    }

    case 'personal-details': {
      const ident = state.form['life-assured-identification'] || {}
      const idCheckboxKeys = [
        'nia-ghana-card',
        'passport',
        'voter-id',
        'dvla',
        'nhis',
        'ssnit',
      ]
      const selectedIdCount = idCheckboxKeys.filter(
        (k) => ident[k] === 'Y'
      ).length

      let effectiveIdType = null
      if (ident['nia-ghana-card'] === 'Y') {
        effectiveIdType = 'National ID'
      } else if (ident.passport === 'Y') {
        effectiveIdType = 'Passport'
      } else if (ident['voter-id'] === 'Y') {
        effectiveIdType = 'Voters ID'
      } else if (ident.dvla === 'Y') {
        effectiveIdType = 'Drivers New ID'
      } else if (ident.nhis === 'Y') {
        effectiveIdType = 'Nhis'
      } else if (ident.ssnit === 'Y') {
        effectiveIdType = 'Ssnit'
      } else if (hasValue(ident['id-type'])) {
        effectiveIdType = ident['id-type']
      }

      validates =
        verify(
          state,
          ident['first-names'],
          (v) => (requiredOnly ? hasValue(v) : hasValue(v) && validName(v)),
          !validateOnly && { id: 'first-names' },
          requiredOnly ? ['*'] : ['Please enter a valid first-name']
        ) && validates

      validates =
        verify(
          state,
          ident.surname,
          (v) => (requiredOnly ? hasValue(v) : hasValue(v) && validName(v)),
          !validateOnly && { id: 'surname' },
          requiredOnly ? ['*'] : ['Please enter a valid surname']
        ) && validates
      ;['gender', 'date-of-birth', 'marital-status'].forEach((field) => {
        validates =
          verify(
            state,
            ident[field],
            hasValue,
            !validateOnly && { id: field },
            ['*']
          ) && validates
      })

      validates =
        verify(
          state,
          ident.ghanaian,
          (v) => hasValue(v) && (v === 'Y' || v === 'N'),
          !validateOnly && { id: 'ghanaian' },
          ['*']
        ) && validates

      if (ident.ghanaian === 'N') {
        validates =
          verify(
            state,
            ident['other-nationality'],
            hasValue,
            !validateOnly && { id: 'other-nationality' },
            ['If you are not a ghanaian please select your nationality']
          ) && validates
      }

      if (ident.gender === 'Female') {
        validates =
          verify(
            state,
            ident.title,
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                ['Miss', 'Ms', 'Mrs', 'Adv', 'Dr', 'Prof', 'Rev'].indexOf(
                  ident.title
                ) >= 0),
            !validateOnly && { id: 'title' },
            requiredOnly
              ? ['*']
              : ['The selected title and gender does not correspond.']
          ) && validates
      }

      if (ident.gender === 'Male') {
        validates =
          verify(
            state,
            ident.title,
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                ['Mr', 'Adv', 'Dr', 'Prof', 'Rev'].indexOf(ident.title) >= 0),
            !validateOnly && { id: 'title' },
            requiredOnly
              ? ['*']
              : ['The selected title and gender does not correspond.']
          ) && validates
      }

      const idChoiceOk =
        selectedIdCount === 1 ||
        (selectedIdCount === 0 && hasValue(ident['id-type']))
      validates =
        verify(
          state,
          idChoiceOk,
          (ok) => ok === true,
          !validateOnly && { id: 'nia-ghana-card' },
          ['Please select exactly one identification type.']
        ) && validates

      const idFormatByType = {
        'Voters ID': validVotersID,
        Ssnit: validSSNIT,
        Passport: validPassport,
        'Drivers New ID': validDrivers,
        'National ID': validID,
        Nhis: validNHIS,
        'Drivers Old ID': validOldDrivers,
      }
      validates =
        verify(
          state,
          ident['idpassport-number'],
          (v) => {
            if (!hasValue(v)) {
              return false
            }
            if (requiredOnly) {
              return true
            }
            const fmt = effectiveIdType && idFormatByType[effectiveIdType]
            return fmt ? fmt(v) : true
          },
          !validateOnly && { id: 'idpassport-number' },
          requiredOnly
            ? ['*']
            : ['Invalid ID Number for chosen ID Type. Please correct.']
        ) && validates

      const postal = state.form['life-assured-postal-address'] || {}
      ;['po-box-or-private-bag', 'town', 'region'].forEach((field) => {
        validates =
          verify(
            state,
            postal[field],
            hasValue,
            !validateOnly && { id: field },
            ['*']
          ) && validates
      })

      const residential = state.form['life-assured-residential-address'] || {}
      validates =
        verify(
          state,
          residential.street,
          hasValue,
          !validateOnly && { id: 'street' },
          ['*']
        ) && validates
      validates =
        verify(
          state,
          residential.suburb,
          hasValue,
          !validateOnly && { id: 'suburb' },
          ['*']
        ) && validates

      const contact = state.form['life-assured-contact-details'] || {}
      validates =
        verify(
          state,
          contact['cell-phone'],
          (v) => (requiredOnly ? hasValue(v) : validCellnumber(v)),
          !validateOnly && { id: 'cell-phone' },
          requiredOnly ? ['*'] : ['Please enter a valid Cellphone Number']
        ) && validates
      validates =
        verify(
          state,
          contact.email,
          (v) => (requiredOnly ? hasValue(v) : validEmail(v)),
          !validateOnly && { id: 'email' },
          requiredOnly ? ['*'] : ['Please enter a valid Email address']
        ) && validates

      const emp = state.form['life-assured-employment-details'] || {}
      validates =
        verify(
          state,
          emp.occupation,
          hasValue,
          !validateOnly && { id: 'occupation' },
          ['*']
        ) && validates

      break
    }

    case 'spouse-details':
      // Spouse details
      validates = Object.keys(state.form['spouse-identification'])
        .filter((k) => ['id-type', 'idpassport-number'].indexOf(k) < 0)
        .reduce(
          (valid, field) =>
            verify(
              state,
              state.form['spouse-identification'][field],
              hasValue,
              !validateOnly && { id: 'idpassport-number' },
              ['*']
            ) && valid,
          validates
        )

      //Validate Spouse birth date
      validates =
        verify(
          state,
          state.form['spouse-identification']['date-of-birth'],
          (v) =>
            hasValue(v) &&
            (requiredOnly ||
              (dayjs().diff(v, 'year') >= 18 && dayjs().diff(v, 'year') <= 59)),
          !validateOnly && { id: 'date-of-birth' },
          requiredOnly
            ? ['*']
            : ['The age is outside the allowable bounds for this product.']
        ) && validates

      //Validate field names without numbers
      validates =
        verify(
          state,
          state.form['spouse-identification']['first-names'],
          (v) => (requiredOnly ? hasValue(v) : validName(v)),
          !validateOnly && { id: 'first-names' },
          requiredOnly ? ['*'] : ['Please enter a valid first-name']
        ) && validates

      validates =
        verify(
          state,
          state.form['spouse-identification']['surname'],
          (v) => (requiredOnly ? hasValue(v) : validName(v)),
          !validateOnly && { id: 'surname' },
          requiredOnly ? ['*'] : ['Please enter a valid surname']
        ) && validates
      //Validate the gender & title
      if (state.form['spouse-identification']['gender'] === 'Female') {
        validates =
          verify(
            state,
            state.form['spouse-identification']['title'],
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                ['Miss', 'Ms', 'Mrs', 'Adv', 'Dr', 'Prof', 'Rev'].indexOf(
                  state.form['spouse-identification']['title']
                ) >= 0),
            !validateOnly && { id: 'title' },
            requiredOnly
              ? ['*']
              : ['The selected title and gender does not correspond.']
          ) && validates
      }

      if (state.form['spouse-identification']['gender'] === 'Male') {
        validates =
          verify(
            state,
            state.form['spouse-identification']['title'],
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                ['Mr', 'Adv', 'Dr', 'Prof', 'Rev'].indexOf(
                  state.form['spouse-identification']['title']
                ) >= 0),
            !validateOnly && { id: 'title' },
            requiredOnly
              ? ['*']
              : ['The selected title and gender does not correspond.']
          ) && validates
      }
      // Cell phone number
      validates =
        verify(
          state,
          state.form['spouse-contact-details']['cell-phone'],
          (v) => (requiredOnly ? hasValue(v) : validCellnumber(v)),
          !validateOnly && { id: 'cell-phone' },
          requiredOnly ? ['*'] : ['Please enter a valid Cellphone Number']
        ) && validates

      //ID
      if (state.form['spouse-identification']['id-type'] === 'National ID') {
        validates =
          verify(
            state,
            state.form['spouse-identification']['idpassport-number'],
            (v) => (requiredOnly ? hasValue(v) : hasValue(v) && validID(v)),
            !validateOnly && { id: 'idpassport-number' },
            requiredOnly
              ? ['*']
              : ['Invalid ID Number for chosen ID Type. Please correct.']
          ) && validates
      }

      //Voters ID
      if (state.form['spouse-identification']['id-type'] === 'Voters ID') {
        validates =
          verify(
            state,
            state.form['spouse-identification']['idpassport-number'],
            (v) =>
              requiredOnly ? hasValue(v) : hasValue(v) && validVotersID(v),
            !validateOnly && { id: 'idpassport-number' },
            requiredOnly
              ? ['*']
              : ['Invalid ID Number for chosen ID Type. Please correct.']
          ) && validates
      }

      //SSNIT
      if (state.form['spouse-identification']['id-type'] === 'Ssnit') {
        validates =
          verify(
            state,
            state.form['spouse-identification']['idpassport-number'],
            (v) => (requiredOnly ? hasValue(v) : hasValue(v) && validSSNIT(v)),
            !validateOnly && { id: 'idpassport-number' },
            requiredOnly
              ? ['*']
              : ['Invalid ID Number for chosen ID Type. Please correct.']
          ) && validates
      }

      //Passport
      if (state.form['spouse-identification']['id-type'] === 'Passport') {
        validates =
          verify(
            state,
            state.form['spouse-identification']['idpassport-number'],
            (v) =>
              requiredOnly ? hasValue(v) : hasValue(v) && validPassport(v),
            !validateOnly && { id: 'idpassport-number' },
            requiredOnly
              ? ['*']
              : ['Invalid ID Number for chosen ID Type. Please correct.']
          ) && validates
      }

      //Drivers
      if (state.form['spouse-identification']['id-type'] === 'Drivers New ID') {
        validates =
          verify(
            state,
            state.form['spouse-identification']['idpassport-number'],
            (v) =>
              requiredOnly ? hasValue(v) : hasValue(v) && validDrivers(v),
            !validateOnly && { id: 'idpassport-number' },
            requiredOnly
              ? ['*']
              : ['Invalid ID Number for chosen ID Type. Please correct.']
          ) && validates
      }

      //NHIS
      if (state.form['spouse-identification']['id-type'] === 'Nhis') {
        validates =
          verify(
            state,
            state.form['spouse-identification']['idpassport-number'],
            (v) => (requiredOnly ? hasValue(v) : hasValue(v) && validNHIS(v)),
            !validateOnly && { id: 'idpassport-number' },
            requiredOnly
              ? ['*']
              : ['Invalid ID Number for chosen ID Type. Please correct.']
          ) && validates
      }

      //Old Drivers
      if (state.form['spouse-identification']['id-type'] === 'Drivers Old ID') {
        validates =
          verify(
            state,
            state.form['spouse-identification']['idpassport-number'],
            (v) =>
              requiredOnly ? hasValue(v) : hasValue(v) && validOldDrivers(v),
            !validateOnly && { id: 'idpassport-number' },
            requiredOnly
              ? ['*']
              : ['Invalid ID Number for chosen ID Type. Please correct.']
          ) && validates
      }
      break

    case 'add-other-lives':
    case 'edit-other-lives':
      validates = [
        'first-names',
        'surname',
        'role',
        'date-of-birth',
        'gender',
      ].reduce(
        (valid, field) =>
          verify(
            state,
            state.local[field],
            hasValue,
            !validateOnly && { id: field },
            ['*']
          ) && valid,
        validates
      )

      //Validate Name Fields To Not Accept Numbers
      validates =
        verify(
          state,
          state.local['first-names'],
          (v) => (requiredOnly ? hasValue(v) : validName(v)),
          !validateOnly && { id: 'first-names' },
          requiredOnly ? ['*'] : ['Please enter a valid first-name']
        ) && validates

      validates =
        verify(
          state,
          state.local['surname'],
          (v) => (requiredOnly ? hasValue(v) : validName(v)),
          !validateOnly && { id: 'surname' },
          requiredOnly ? ['*'] : ['Please enter a valid surname']
        ) && validates

      //Validate the DOB to not be selected in the future
      validates =
        verify(
          state,
          state.local['date-of-birth'],
          (v) => hasValue(v) && (requiredOnly || dayjs().isAfter(v, now)),
          !validateOnly && { id: 'date-of-birth' },
          requiredOnly
            ? ['*']
            : ['Please ensure the date of birth is not selected in the future.']
        ) && validates

      //Validate the gender & role
      if (state.local['gender'] === 'Female') {
        validates =
          verify(
            state,
            state.local['role'],
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                [
                  'Child',
                  'Cousin',
                  'Daughter',
                  'Family',
                  'Grandchild',
                  'Grandmother',
                  'Main',
                  'Mother',
                  'Mother-in-law',
                  'Niece',
                  'Sister',
                  'Spouse',
                  'Step-Mother',
                ].indexOf(state.local['role']) >= 0),
            !validateOnly && { id: 'role' },
            requiredOnly
              ? ['*']
              : ["Role and gender combination doesn't match. Please correct."]
          ) && validates
      }

      if (state.local['gender'] === 'Male') {
        validates =
          verify(
            state,
            state.local['role'],
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                [
                  'Brother',
                  'Child',
                  'Cousin',
                  'Family',
                  'Father',
                  'Father-in-law',
                  'Grandchild',
                  'Grandfather',
                  'Main',
                  'Nephew',
                  'Son',
                  'Spouse',
                  'Step-Father',
                ].indexOf(state.local['role']) >= 0),
            !validateOnly && { id: 'role' },
            requiredOnly
              ? ['*']
              : ["Role and gender combination doesn't match. Please correct."]
          ) && validates
      }

      if (
        state.local['role'] === 'Child' ||
        state.local['role'] === 'Daughter' ||
        state.local['role'] === 'Son' ||
        state.local['role'] === 'Grandchild'
      ) {
        validates =
          verify(
            state,
            state.local['date-of-birth'],
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                (dayjs().diff(v, 'year') >= 0 &&
                  dayjs().diff(v, 'year') <= 21 &&
                  dayjs().isAfter(v, now))),
            !validateOnly && { id: 'date-of-birth' },
            requiredOnly
              ? ['*']
              : ['The age is outside the allowable bounds for this product.']
          ) && validates
      }

      //Handle birth dates selected in the future
      if (
        state.local['role'] === 'Brother' ||
        state.local['role'] === 'Sister'
      ) {
        validates =
          verify(
            state,
            state.local['date-of-birth'],
            (v) => hasValue(v) && (requiredOnly || dayjs().isAfter(v, now)),
            !validateOnly && { id: 'date-of-birth' },
            requiredOnly ? ['*'] : ['Invalid Birth Date.']
          ) && validates
      }

      //Validate Parents & Grandparents Age Limits
      if (
        state.local['role'] === 'Mother' ||
        state.local['role'] === 'Father' ||
        state.local['role'] === 'Mother-in-law' ||
        state.local['role'] === 'Father-in-law' ||
        state.local['role'] === 'Step-father' ||
        state.local['role'] === 'Step-mother' ||
        state.local['role'] === 'Grandmother' ||
        state.local['role'] === 'Grandfather'
      ) {
        const mainAge =
          state.form['life-assured-identification']['date-of-birth']
        const age = dayjs().diff(mainAge, 'year')
        validates =
          verify(
            state,
            state.local['date-of-birth'],
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                (dayjs().diff(v, 'year') >= age + 13 &&
                  dayjs().diff(v, 'year') <= 74)),
            !validateOnly && { id: 'date-of-birth' },
            requiredOnly
              ? ['*']
              : ['The age is outside the allowable bounds for this product.']
          ) && validates
      }

      //Validate Guardian Age Limit
      if (state.local['role'] === 'Guardian') {
        const mainAge =
          state.form['life-assured-identification']['date-of-birth']
        const age = dayjs().diff(mainAge, 'year')
        validates =
          verify(
            state,
            state.local['date-of-birth'],
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                (dayjs().diff(v, 'year') >= age + 16 &&
                  dayjs().diff(v, 'year') <= 74)),
            !validateOnly && { id: 'date-of-birth' },
            requiredOnly
              ? ['*']
              : ['The age is outside the allowable bounds for this product.']
          ) && validates
      }

      //Validate Spouse Age Limit
      if (state.local['role'] === 'Spouse') {
        validates =
          verify(
            state,
            state.local['date-of-birth'],
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                (dayjs().diff(v, 'year') >= 18 &&
                  dayjs().diff(v, 'year') <= 59)),
            !validateOnly && { id: 'date-of-birth' },
            requiredOnly
              ? ['*']
              : ['The age is outside the allowable bounds for this product.']
          ) && validates
      }
      break

    case 'trustee-details':
      validates = Object.keys(state.form['trustee-identification'])
        .filter(
          (k) =>
            ['marital-status', 'idpassport-number', 'id-type'].indexOf(k) < 0
        )
        .reduce(
          (valid, field) =>
            verify(
              state,
              state.form['trustee-identification'][field],
              hasValue,
              !validateOnly && { id: field },
              ['*']
            ) && valid,
          validates
        )

      //Validate field names without numbers
      validates =
        verify(
          state,
          state.form['trustee-identification']['first-names'],
          (v) => (requiredOnly ? hasValue(v) : validName(v)),
          !validateOnly && { id: 'first-names' },
          requiredOnly ? ['*'] : ['Please enter a valid first-name']
        ) && validates

      validates =
        verify(
          state,
          state.form['trustee-identification']['surname'],
          (v) => (requiredOnly ? hasValue(v) : validName(v)),
          !validateOnly && { id: 'surname' },
          requiredOnly ? ['*'] : ['Please enter a valid surname']
        ) && validates

      // Validate age
      validates =
        verify(
          state,
          state.form['trustee-identification']['date-of-birth'],
          (v) => hasValue(v) && (requiredOnly || dayjs().diff(v, 'year') >= 18),
          !validateOnly && { id: 'date-of-birth' },
          requiredOnly
            ? ['*']
            : ['The age is outside the allowable bounds for this product.']
        ) && validates

      //Validate the gender & title
      if (state.form['trustee-identification'].gender === 'Female') {
        validates =
          verify(
            state,
            state.form['trustee-identification'].title,
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                ['Miss', 'Ms', 'Mrs', 'Adv', 'Dr', 'Prof', 'Rev'].indexOf(
                  state.form['trustee-identification'].title
                ) >= 0),
            !validateOnly && { id: 'title' },
            requiredOnly
              ? ['*']
              : ["Title and Gender combination doesn't match. Please correct."]
          ) && validates
      }

      if (state.form['trustee-identification'].gender === 'Male') {
        validates =
          verify(
            state,
            state.form['trustee-identification'].title,
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                ['Mr', 'Adv', 'Dr', 'Prof', 'Rev'].indexOf(
                  state.form['trustee-identification'].title
                ) >= 0),
            !validateOnly && { id: 'title' },
            requiredOnly
              ? ['*']
              : ["Title and Gender combination doesn't match. Please correct."]
          ) && validates
      }

      // Cell phone number
      validates =
        verify(
          state,
          state.form['trustee-contact-details']['cell-phone'],
          (v) => (requiredOnly ? hasValue(v) : validCellnumber(v)),
          !validateOnly && { id: 'cell-phone' },
          requiredOnly ? ['*'] : ['Please enter a valid Cellphone Number']
        ) && validates

      //Validate ID types if selected.

      if (state.form['trustee-identification']['id-type'] === 'Ghana Card') {
        validates =
          verify(
            state,
            state.form['trustee-identification']['idpassport-number'],
            (v) => (requiredOnly ? hasValue(v) : hasValue(v) && validID(v)),
            !validateOnly && { id: 'idpassport-number' },
            requiredOnly
              ? ['*']
              : ['Invalid ID Number for chosen ID Type. Please correct.']
          ) && validates
      }

      //Voter id
      if (state.form['trustee-identification']['id-type'] === 'Voter id') {
        validates =
          verify(
            state,
            state.form['trustee-identification']['idpassport-number'],
            (v) =>
              requiredOnly ? hasValue(v) : hasValue(v) && validVotersID(v),
            !validateOnly && { id: 'idpassport-number' },
            requiredOnly
              ? ['*']
              : ['Invalid ID Number for chosen ID Type. Please correct.']
          ) && validates
      }

      //Ssnit
      if (state.form['trustee-identification']['id-type'] === 'Ssnit') {
        validates =
          verify(
            state,
            state.form['trustee-identification']['idpassport-number'],
            (v) => (requiredOnly ? hasValue(v) : hasValue(v) && validSSNIT(v)),
            !validateOnly && { id: 'idpassport-number' },
            requiredOnly
              ? ['*']
              : ['Invalid ID Number for chosen ID Type. Please correct.']
          ) && validates
      }

      //Passport
      if (state.form['trustee-identification']['id-type'] === 'Passport') {
        validates =
          verify(
            state,
            state.form['trustee-identification']['idpassport-number'],
            (v) =>
              requiredOnly ? hasValue(v) : hasValue(v) && validPassport(v),
            !validateOnly && { id: 'idpassport-number' },
            requiredOnly
              ? ['*']
              : ['Invalid ID Number for chosen ID Type. Please correct.']
          ) && validates
      }

      //Drivers
      if (
        state.form['trustee-identification']['id-type'] === 'Drivers New id'
      ) {
        validates =
          verify(
            state,
            state.form['trustee-identification']['idpassport-number'],
            (v) =>
              requiredOnly ? hasValue(v) : hasValue(v) && validDrivers(v),
            !validateOnly && { id: 'idpassport-number' },
            requiredOnly
              ? ['*']
              : ['Invalid ID Number for chosen ID Type. Please correct.']
          ) && validates
      }

      //NHIS
      if (state.form['trustee-identification']['id-type'] === 'Nhis') {
        validates =
          verify(
            state,
            state.form['trustee-identification']['idpassport-number'],
            (v) => (requiredOnly ? hasValue(v) : hasValue(v) && validNHIS(v)),
            !validateOnly && { id: 'idpassport-number' },
            requiredOnly
              ? ['*']
              : ['Invalid ID Number for chosen ID Type. Please correct.']
          ) && validates
      }

      //Old Drivers
      if (
        state.form['trustee-identification']['id-type'] === 'Drivers Old id'
      ) {
        validates =
          verify(
            state,
            state.form['trustee-identification']['idpassport-number'],
            (v) =>
              requiredOnly ? hasValue(v) : hasValue(v) && validOldDrivers(v),
            !validateOnly && { id: 'idpassport-number' },
            requiredOnly
              ? ['*']
              : ['Invalid ID Number for chosen ID Type. Please correct.']
          ) && validates
      }
      break

    case 'additional-lives':
      validates = (state.form['additional-lives'] || []).reduce(
        (valid, life, counter) => {
          valid = ['first-names', 'surname', 'date-of-birth'].reduce(
            (vld, field) => vld && hasValue(life[field]),
            valid
          )
          if (!requiredOnly && valid && hasValue(life['age-of-member'])) {
            valid =
              verify(
                state,
                life['date-of-birth'],
                (v) => {
                  if (!hasValue(v)) return false
                  const d = dayjs(v, ['YYYYMMDD', 'YYYY-MM-DD'])
                  if (!d.isValid()) return false
                  return (
                    dayjs().diff(d, 'year').toString() ===
                    String(life['age-of-member']).trim()
                  )
                },
                !validateOnly && { id: `date-of-birth-${counter}` },
                [
                  `Date of birth does not match indicated age of ${
                    life['age-of-member']
                  } for ${life.relationship || life.role || 'this life'}.`,
                ]
              ) && valid
          }
          if (!requiredOnly && valid) {
            valid =
              verify(
                state,
                life['first-names'],
                (v) => (requiredOnly ? hasValue(v) : validName(v)),
                !validateOnly && { id: `first-names-${counter}` },
                requiredOnly ? ['*'] : ['Please enter a valid first-name']
              ) && valid
          }
          if (!requiredOnly && valid) {
            valid =
              verify(
                state,
                life.surname,
                (v) => (requiredOnly ? hasValue(v) : validName(v)),
                !validateOnly && { id: `surname-${counter}` },
                requiredOnly ? ['*'] : ['Please enter a valid surname']
              ) && valid
          }
          return valid
        },
        validates
      )
      break

    case 'add-beneficiary':
    case 'edit-beneficiary':
      validates = [
        'first-names',
        'surname',
        'role',
        'gender',
        'date-of-birth',
      ].reduce(
        (valid, field) =>
          verify(
            state,
            state.local[field],
            hasValue,
            !validateOnly && { id: field },
            ['*']
          ) && valid,
        validates
      )

      //Validate Name Fields To Not Accept Numbers
      validates =
        verify(
          state,
          state.local['first-names'],
          (v) => (requiredOnly ? hasValue(v) : validName(v)),
          !validateOnly && { id: 'first-names' },
          requiredOnly ? ['*'] : ['Please enter a valid first-name']
        ) && validates

      validates =
        verify(
          state,
          state.local['surname'],
          (v) => (requiredOnly ? hasValue(v) : validName(v)),
          !validateOnly && { id: 'surname' },
          requiredOnly ? ['*'] : ['Please enter a valid surname']
        ) && validates

      //Validate the gender & role
      if (state.local['gender'] === 'Female') {
        validates =
          verify(
            state,
            state.local['role'],
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                [
                  'Child',
                  'Cousin',
                  'Daughter',
                  'Family',
                  'Grandchild',
                  'Grandmother',
                  'Main',
                  'Mother',
                  'Mother-in-law',
                  'Niece',
                  'Sister',
                  'Spouse',
                  'Step-Mother',
                ].indexOf(state.local['role']) >= 0),
            !validateOnly && { id: 'role' },
            requiredOnly
              ? ['*']
              : ["Role and gender combination doesn't match. Please correct."]
          ) && validates
      }

      if (state.local['gender'] === 'Male') {
        validates =
          verify(
            state,
            state.local['role'],
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                [
                  'Brother',
                  'Child',
                  'Cousin',
                  'Family',
                  'Father',
                  'Father-in-law',
                  'Grandchild',
                  'Grandfather',
                  'Main',
                  'Nephew',
                  'Son',
                  'Spouse',
                  'Step-Father',
                ].indexOf(state.local['role']) >= 0),
            !validateOnly && { id: 'role' },
            requiredOnly
              ? ['*']
              : ["Role and gender combination doesn't match. Please correct."]
          ) && validates
      }
      break

    case 'update-beneficiary-split-percentage':
      if (!requiredOnly) {
        validates =
          Math.ceil(
            Object.keys(state.local || {})
              .filter((k) => k.startsWith('beneficiary-split'))
              .reduce((t, b) => {
                const v = parseFloat(state.local[b])
                return isNaN(v) ? -1.0 : v + t
              }, 0.0)
          ) === 100 && validates
        if (!validateOnly) {
          updateItemIn(
            state.page.item,
            { id: 'update-benefit-split-percentage' },
            {
              errors: validates ? [] : ['The allocation must add up to 100.00'],
            }
          )
        }
      }
      break

    case 'medical-underwriting':
      validates = [
        'planning-other-insurance',
        'other-assurance-past12-months',
        'risky-occupation-hobbies',
        'refused-application-or-increased-premium',
        'insolvency-issue',
        'occupation-not-clerical',
      ].reduce((valid, field) => {
        valid =
          verify(
            state,
            state.form['life-assured-general-habits'][field],
            hasValue,
            !validateOnly && { id: field },
            ['*']
          ) && valid
        if (state.form['life-assured-general-habits'][field] === 'Yes') {
          let details = `${field}-details`
          valid =
            verify(
              state,
              state.form['life-assured-general-habits'][details],
              hasValue,
              !validateOnly && { id: details },
              ['*']
            ) && valid
        }
        return valid
      }, validates)

      validates =
        verify(
          state,
          state.form['life-assured-general-habits'][
            'cover-exceeds-one-hundred-thousand'
          ],
          hasValue,
          !validateOnly && { id: 'cover-exceeds-one-hundred-thousand' },
          ['*']
        ) && validates

      if (
        state.form['life-assured-general-habits'][
          'cover-exceeds-one-hundred-thousand'
        ] === 'Yes'
      ) {
        validates =
          verify(
            state,
            state.form['life-assured-general-habits'][
              'average-monthly-gross-income'
            ],
            hasValue,
            !validateOnly && { id: 'average-monthly-gross-income' },
            ['*']
          ) && validates
      }

      validates =
        verify(
          state,
          state.form['life-assured-general-habits']['smoker'] === 'Y' ||
            state.form['life-assured-general-habits']['non-smoker'] === 'Y' ||
            state.form['life-assured-general-habits']['never-smoked'] === 'Y',
          hasValue,
          !validateOnly && { id: 'smoker-options-radio-group' },
          ['*']
        ) && validates

      let age = dayjs().diff(
        state.form['life-assured-identification']['date-of-birth'],
        'year'
      )

      if (state.form['life-assured-general-habits'].smoker === 'Y') {
        let yrs = parseInt(
          state.form['life-assured-general-habits']['years-smoking']
        )

        validates =
          verify(
            state,
            state.form['life-assured-general-habits']['years-smoking'],
            (v) =>
              requiredOnly
                ? hasValue(v)
                : hasValue(v) && !isNaN(yrs) && yrs > 0 && yrs <= age,
            !validateOnly && { id: 'years-smoking' },
            requiredOnly ? ['*'] : ['Please enter a valid amount of years']
          ) && validates

        validates = [
          'cigarettes-per-day',
          'cigars-per-day',
          'pipe',
          'medical-advice-stop-smoking',
        ].reduce(
          (valid, field) =>
            verify(
              state,
              state.form['life-assured-general-habits'][field],
              hasValue,
              !validateOnly && { id: field },
              ['*']
            ) && valid,
          validates
        )
      }

      if (state.form['life-assured-general-habits']['non-smoker'] === 'Y') {
        let yrs = parseInt(
          state.form['life-assured-general-habits']['years-not-smoking']
        )
        validates =
          verify(
            state,
            state.form['life-assured-general-habits']['years-not-smoking'],
            (v) =>
              requiredOnly
                ? hasValue(v)
                : hasValue(v) && !isNaN(yrs) && yrs > 0 && yrs <= age,
            !validateOnly && { id: 'years-not-smoking' },
            requiredOnly ? ['*'] : ['Please enter a valid amount of years']
          ) && validates
      }

      validates = [
        'medical-advice-reduce-drinking',
        'excessive-drinking',
        'excessive-drinking-past',
      ].reduce(
        (valid, field) =>
          verify(
            state,
            state.form['life-assured-general-habits'][field],
            hasValue,
            !validateOnly && { id: field },
            ['*']
          ) && valid,
        validates
      )
      break

    case 'medical-declaration':
      validates = Object.keys(state.form['life-assured-health-details'])
        .filter(
          (k) =>
            [
              'family-history-of-illness',
              'family-history-of-illness-details',
            ].indexOf(k) < 0
        )
        .reduce(
          (valid, field) =>
            verify(
              state,
              state.form['life-assured-health-details'][field],
              hasValue,
              !validateOnly && { id: field },
              ['*']
            ) && valid,
          validates
        )

      validates = ['family-history-of-illness'].reduce((valid, field) => {
        valid =
          verify(
            state,
            state.form['life-assured-health-details'][field],
            hasValue,
            !validateOnly && { id: field },
            ['*']
          ) && valid
        if (state.form['life-assured-health-details'][field] === 'Yes') {
          let details = `${field}-details`
          valid =
            verify(
              state,
              state.form['life-assured-health-details'][details],
              hasValue,
              !validateOnly && { id: details },
              ['*']
            ) && valid
        }
        return valid
      }, validates)

      break
    case 'health-descriptions':
      validates = state.form['health-concerns'].reduce(
        (itemValid, item, index) =>
          Object.keys(item).reduce(
            (fieldValid, field) =>
              verify(
                state,
                item[field],
                hasValue,
                !validateOnly && { id: `${field}-${index + 1}` },
                ['*']
              ) && fieldValid,
            itemValid
          ),
        validates
      )
      break

    case 'payment-details':
      validates =
        verify(
          state,
          state.form['payment-method-selection']['debit-order'] ||
            state.form['payment-method-selection']['stop-order'],
          hasValue,
          !validateOnly && { id: 'payment-method-radio-group' },
          ['*']
        ) && validates

      validates =
        verify(
          state,
          state.form['payment-start-date'],
          hasValue,
          !validateOnly && { id: 'payment-start-date' },
          ['*']
        ) && validates

      validates =
        verify(
          state,
          state.form['payment-frequency'],
          hasValue,
          !validateOnly && { id: 'payment-frequency' },
          ['*']
        ) && validates
      break

    case 'spouse-identification-for-payment':
      validates = Object.keys(state.form['payer-person-identification']).reduce(
        (valid, field) =>
          verify(
            state,
            state.local[field],
            hasValue,
            !validateOnly && { id: field },
            ['*']
          ) && valid,
        validates
      )

      //Validate Field Names to only be letters
      validates =
        verify(
          state,
          state.local['first-names'],
          (v) => (requiredOnly ? hasValue(v) : validName(v)),
          !validateOnly && { id: 'first-names' },
          requiredOnly ? ['*'] : ['Please enter a valid first-name']
        ) && validates

      validates =
        verify(
          state,
          state.local['surname'],
          (v) => (requiredOnly ? hasValue(v) : validName(v)),
          !validateOnly && { id: 'surname' },
          requiredOnly ? ['*'] : ['Please enter a valid surname']
        ) && validates

      //Validate the DOB to not be selected in the future
      validates =
        verify(
          state,
          state.local['date-of-birth'],
          (v) => hasValue(v) && (requiredOnly || dayjs().isAfter(v, now)),
          !validateOnly && { id: 'date-of-birth' },
          requiredOnly ? ['*'] : ['Invalid birth date.']
        ) && validates
      break

    case 'debit-order-details':
      validates = Object.keys(state.form['payment-bank-details']).reduce(
        (valid, field) =>
          verify(
            state,
            state.form['payment-bank-details'][field],
            hasValue,
            !validateOnly && { id: field },
            ['*']
          ) && valid,
        validates
      )
      validates =
        verify(
          state,
          state.form['payer-signature'],
          hasValue,
          !validateOnly && { id: 'interactive-canvas-html' },
          requiredOnly ? ['*'] : ['Your signature is required']
        ) && validates

      //Validate field name to have no numbers
      validates =
        verify(
          state,
          state.form['payment-bank-details']['account-holder-name'],
          (v) => (requiredOnly ? hasValue(v) : validName(v)),
          !validateOnly && { id: 'account-holder-name' },
          requiredOnly ? ['*'] : ['Please enter a valid name']
        ) && validates

      //VALIDATE ACCOUNT NUMBER LENGTH BASED ON THE ACCOUNT ENTITY NAME CHOSEN
      if (!requiredOnly) {
        //10 DIGITS
        if (
          (state.form['payment-bank-details']['account-entity-name'] ===
            'Absa (gh) Ltd' ||
            state.form['payment-bank-details']['account-entity-name'] ===
              'Zenith Bank') &&
          state.form['payment-bank-details']['account-number'] !== null
        ) {
          validates =
            verify(
              state,
              state.form['payment-bank-details']['account-number'],
              (v) => v.length === 10,
              !validateOnly && { id: 'account-number' },
              ['Please enter a valid account number']
            ) && validates
        }
        //9 / 10 / 11 DIGITS
        else if (
          state.form['payment-bank-details']['account-entity-name'] ===
            'Bank Of Africa' &&
          state.form['payment-bank-details']['account-number'] !== null
        ) {
          validates =
            verify(
              state,
              state.form['payment-bank-details']['account-number'],
              (v) => v.length === 9 || v.length === 10 || v.length === 11,
              !validateOnly && { id: 'account-number' },
              ['Please enter a valid account number']
            ) && validates
        }
        //13 DIGITS
        else if (
          (state.form['payment-bank-details']['account-entity-name'] ===
            'Access Bank Ghana Ltd' ||
            state.form['payment-bank-details']['account-entity-name'] ===
              'Consolidated Bank Ghana' ||
            state.form['payment-bank-details']['account-entity-name'] ===
              'Ecobank Ghana Ltd' ||
            state.form['payment-bank-details']['account-entity-name'] ===
              'Fidelity Bank Ghana' ||
            state.form['payment-bank-details']['account-entity-name'] ===
              'First Atlantic Bank Ltd' ||
            state.form['payment-bank-details']['account-entity-name'] ===
              'First Bank Nigeria' ||
            state.form['payment-bank-details']['account-entity-name'] ===
              'Gcb Bank Ltd' ||
            state.form['payment-bank-details']['account-entity-name'] ===
              'National Investment Bank' ||
            state.form['payment-bank-details']['account-entity-name'] ===
              'Omni Bank Ghana Ltd' ||
            state.form['payment-bank-details']['account-entity-name'] ===
              'Prudential Bank Ltd' ||
            state.form['payment-bank-details']['account-entity-name'] ===
              'Republic Bank (gh) Ltd' ||
            state.form['payment-bank-details']['account-entity-name'] ===
              'Stanbic Bank Ghana Ltd' ||
            state.form['payment-bank-details']['account-entity-name'] ===
              'Standard Chartered Bank' ||
            state.form['payment-bank-details']['account-entity-name'] ===
              'Universal Merchant Bank (gh) Ltd') &&
          state.form['payment-bank-details']['account-number'] !== null
        ) {
          validates =
            verify(
              state,
              state.form['payment-bank-details']['account-number'],
              (v) => v.length === 13,
              !validateOnly && { id: 'account-number' },
              ['Please enter a valid account number']
            ) && validates
        }
        //14 DIGITS
        else if (
          state.form['payment-bank-details']['account-entity-name'] ===
          'United Bank For Africa (gh) Ltd'
        ) {
          validates =
            verify(
              state,
              state.form['payment-bank-details']['account-number'],
              (v) => v.length === 14,
              !validateOnly && { id: 'account-number' },
              ['Please enter a valid account number']
            ) && validates
        }
        //12 / 13 DIGITS
        else if (
          (state.form['payment-bank-details']['account-entity-name'] ===
            'Cal Bank Ltd' ||
            state.form['payment-bank-details']['account-entity-name'] ===
              'Societe General Ghana Ltd' ||
            state.form['payment-bank-details']['account-entity-name'] ===
              'Guaranty Trust (gh) Ltd') &&
          state.form['payment-bank-details']['account-number'] !== null
        ) {
          validates =
            verify(
              state,
              state.form['payment-bank-details']['account-number'],
              (v) => v.length === 12 || v.length === 13,
              !validateOnly && { id: 'account-number' },
              ['Please enter a valid account number']
            ) && validates
        }
        //16 DIGITS
        else if (
          state.form['payment-bank-details']['account-entity-name'] ===
            'Agricultural Development Bank' &&
          state.form['payment-bank-details']['account-number'] !== null
        ) {
          validates =
            verify(
              state,
              state.form['payment-bank-details']['account-number'],
              (v) => v.length === 16,
              !validateOnly && { id: 'account-number' },
              ['Please enter a valid account number']
            ) && validates
        }
      }
      break

    case 'stop-order-details':
      validates = Object.keys(state.form['payment-employment-details']).reduce(
        (valid, field) =>
          verify(
            state,
            state.form['payment-employment-details'][field],
            hasValue,
            !validateOnly && { id: field },
            ['*']
          ) && valid,
        validates
      )
      validates =
        verify(
          state,
          state.form['payer-signature'],
          hasValue,
          !validateOnly && { id: 'interactive-canvas-html' },
          requiredOnly ? ['*'] : ['Your signature is required']
        ) && validates
      break

    case 'upload-id-document':
      const section = state.step.substring(7)
      validates = Object.keys(state.form[section]).reduce(
        (valid, field) =>
          verify(
            state,
            state.form[section][field],
            hasValue,
            !validateOnly && { id: field },
            ['*']
          ) && valid,
        validates
      )
      break

    case 'forces-pay-office-additional-information':
      validates =
        verify(
          state,
          state.form['forces-pay-office-details']['armed-forces-rank'],
          hasValue,
          !validateOnly && { id: 'armed-forces-rank' },
          ['*']
        ) && validates
      validates =
        verify(
          state,
          state.form['forces-pay-office-details'].unit,
          hasValue,
          !validateOnly && { id: 'unit' },
          ['*']
        ) && validates
      const isGhanaCardFpo =
        state.form['life-assured-identification']['nia-ghana-card'] === 'Y'
      if (!isGhanaCardFpo) {
        validates =
          verify(
            state,
            state.form['forces-pay-office-details']['ecowas-id-number'],
            (v) => (requiredOnly ? hasValue(v) : hasValue(v) && validID(v)),
            !validateOnly && { id: 'ecowas-id-number' },
            requiredOnly ? ['*'] : ['Please enter a valid ECOWAS ID Number']
          ) && validates
      }
      break

    case 'payment-details-verification':
      validates =
        verify(
          state,
          state.form['payer-signature'],
          hasValue,
          !validateOnly && { id: 'interactive-canvas-html' },
          requiredOnly ? ['*'] : ['Your signature is required']
        ) && validates
      break

    case 'upload-documents':
      if (isForcesPayOfficeForm(state.form)) {
        const svcDocs =
          state.form.documents &&
          state.form.documents['service-certificate-document']
        validates =
          verify(
            state,
            svcDocs,
            (v) => Array.isArray(v) && v.length > 0,
            !validateOnly && { id: 'upload-service-certificate-document' },
            requiredOnly ? ['*'] : ['Service Certificate Document is required']
          ) && validates
      }
      break

    case 'acceptance-screen':
      validates =
        verify(
          state,
          state.form['acceptance-signature'],
          hasValue,
          !validateOnly && { id: 'interactive-canvas-html' },
          requiredOnly ? ['*'] : ['Your signature is required']
        ) && validates
      break
  }

  if (!requiredOnly && !validates) {
    state.local = state.local || {}
    state.local.failed_validation = true
  }
  return validates
}
